/**
 * BrainDumpTree — folder hierarchy + promotion suggestions panel.
 *
 * A13-N14. Sub-view within BrainDumpPage (tab navigation).
 *
 * Props:
 *  - onNoteSelect: (noteId: string) => void
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import { useConfirmDialog } from '../overlay/ConfirmDialog';
import { AnimatedList } from '../motion';
import PromotionSuggestionsPanel from './PromotionSuggestionsPanel';

const API_PREFIX = '/api/v1/aif/brain-dump';

function FolderNode({ folder, depth = 0, onSelect, selectedId }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = folder.id === selectedId;

  return (
    <div>
      <div
        onClick={() => onSelect(folder)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onSelect(folder)}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        className={`flex items-center gap-1 py-1.5 px-2 rounded cursor-pointer text-sm transition ${
          isSelected
            ? 'bg-[var(--aif-color-info-light)] text-[var(--aif-color-info)]'
            : 'hover:bg-[var(--aif-bg-surface)]'
        }`}
        data-testid={`folder-${folder.id}`}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="w-4 text-[var(--aif-text-muted)] text-xs"
          >
            {expanded ? '\u25BC' : '\u25B6'}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span>{folder.icon || '\uD83D\uDCC1'}</span>
        <span className="flex-1 truncate text-[var(--aif-text-primary)]">{folder.name}</span>
        {folder.note_count > 0 && (
          <span className="text-xs text-[var(--aif-text-muted)]">{folder.note_count}</span>
        )}
        {folder.visibility === 'tenant_shared' && (
          <span title="Geteilt" className="text-xs">\uD83D\uDC65</span>
        )}
      </div>
      {expanded && hasChildren && (
        <div>
          {folder.children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              depth={depth + 1}
              onSelect={onSelect}
              selectedId={selectedId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function BrainDumpTree({ onNoteSelect }) {
  const { t } = useTranslation();
  const { confirm, ConfirmDialogElement } = useConfirmDialog();
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderNotes, setFolderNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${API_PREFIX}/folders`);
      const data = res.data ?? res;
      setFolders(data.folders || []);
    } catch (err) {
      setError(err.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadFolders(); }, [loadFolders]);

  // Load notes when folder selected
  useEffect(() => {
    if (!selectedFolder) { setFolderNotes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`${API_PREFIX}/notes?folder_id=${selectedFolder.id}&limit=50`);
        if (!cancelled) {
          const data = res.data ?? res;
          setFolderNotes(data.notes || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || t('brainDump.loadError', 'Notizen konnten nicht geladen werden'));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedFolder]);

  async function handleCreateFolder() {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await apiClient.post(`${API_PREFIX}/folders`, {
        name,
        parent_folder_id: selectedFolder?.id || null,
      });
      setNewFolderName('');
      setShowCreate(false);
      await loadFolders();
    } catch (err) {
      setError(err.message || 'Create failed');
    }
  }

  async function handleDeleteFolder() {
    if (!selectedFolder) return;
    const ok = await confirm({
      title: t('brainDump.deleteFolderConfirm', `Ordner "${selectedFolder.name}" wirklich löschen?`),
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiClient.delete(`${API_PREFIX}/folders/${selectedFolder.id}`);
      setSelectedFolder(null);
      await loadFolders();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  }

  return (
    <div className="space-y-6" data-testid="tree-view">
      {/* Promotion Suggestions */}
      <div>
        <h2 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
          {t('brainDump.promotionTitle', 'Vorschl\u00E4ge')}
        </h2>
        <PromotionSuggestionsPanel embedded />
      </div>

      {/* Folder management bar */}
      <div className="flex items-center gap-2">
        <h2 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide flex-1">
          {t('brainDump.folders', 'Ordner')}
        </h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="text-xs text-[var(--aif-color-primary)] hover:underline"
          data-testid="create-folder-btn"
        >
          + {t('brainDump.newFolder', 'Ordner')}
        </button>
      </div>

      {showCreate && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            placeholder={selectedFolder
              ? t('brainDump.subfolderPlaceholder', `Unter "${selectedFolder.name}"...`)
              : t('brainDump.folderPlaceholder', 'Neuer Ordner...')}
            autoFocus
            className="flex-1 px-3 py-2 border border-[var(--aif-border-default)] rounded text-sm bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--aif-color-primary)]"
            data-testid="folder-name-input"
          />
          <button
            onClick={handleCreateFolder}
            className="px-3 py-2 bg-[var(--aif-color-primary)] text-white rounded text-sm hover:opacity-90 min-h-[36px]"
            data-testid="folder-create-submit"
          >
            {t('common.create', 'Erstellen')}
          </button>
          <button
            onClick={() => { setShowCreate(false); setNewFolderName(''); }}
            className="px-3 py-2 text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] rounded text-sm"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
        </div>
      )}

      {error && (
        <div className="bg-[var(--aif-color-danger-light)] border border-[var(--aif-color-danger-border)] text-[var(--aif-color-danger)] px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-12 gap-4">
        {/* Left: folder tree */}
        <div className="md:col-span-5 border border-[var(--aif-border-default)] rounded-lg bg-[var(--aif-bg-surface)] p-2 max-h-[60vh] overflow-y-auto" data-testid="folder-tree">
          {loading ? (
            <div className="text-[var(--aif-text-muted)] text-sm p-2 animate-pulse">{t('common.loading', 'Lade...')}</div>
          ) : folders.length === 0 ? (
            <div className="text-[var(--aif-text-muted)] text-sm p-2 text-center" data-testid="folders-empty">
              {t('brainDump.noFolders', 'Keine Ordner. Erstelle den ersten oben.')}
            </div>
          ) : (
            folders.map((f) => (
              <FolderNode
                key={f.id}
                folder={f}
                selectedId={selectedFolder?.id}
                onSelect={setSelectedFolder}
              />
            ))
          )}
        </div>

        {/* Right: folder contents */}
        <div className="md:col-span-7">
          {selectedFolder ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[var(--aif-text-primary)]">
                  {selectedFolder.icon || '\uD83D\uDCC1'} {selectedFolder.name}
                </h3>
                <button
                  onClick={handleDeleteFolder}
                  className="text-xs text-[var(--aif-color-danger)] hover:underline"
                  data-testid="delete-folder-btn"
                >
                  {t('brainDump.deleteFolder', 'L\u00F6schen')}
                </button>
              </div>
              {folderNotes.length === 0 ? (
                <div className="text-[var(--aif-text-muted)] text-sm py-6 text-center border-2 border-dashed border-[var(--aif-border-default)] rounded-lg">
                  {t('brainDump.emptyFolder', 'Keine Notizen in diesem Ordner.')}
                </div>
              ) : (
                <AnimatedList className="space-y-2">
                  {folderNotes.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => onNoteSelect(n.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && onNoteSelect(n.id)}
                      className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-3 cursor-pointer hover:shadow-sm transition"
                    >
                      <p className="text-sm text-[var(--aif-text-primary)] line-clamp-2">
                        {n.structured_summary || n.raw_text || t('brainDump.processing', 'Wird verarbeitet...')}
                      </p>
                    </div>
                  ))}
                </AnimatedList>
              )}
            </div>
          ) : (
            <div className="text-[var(--aif-text-muted)] text-sm py-8 text-center" data-testid="no-folder-selected">
              {t('brainDump.selectFolder', 'W\u00E4hle einen Ordner aus.')}
            </div>
          )}
        </div>
      </div>
      {ConfirmDialogElement}
    </div>
  );
}
